import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  TextField,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Stack,
  Chip,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import CloseIcon from "@mui/icons-material/Close";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import axios from "../../../config/axiosConfig";
import { useAuth } from "../../../context/AuthContext";
import MuiAlert from "@mui/material/Alert";
import CentroEquipoDetalleDialog from "../../requerimiento/components/CentroEquipoDetalleDialog";

// Estado 1-2 (Emitido/Detalle) de una OT de Venta (tipo_ot=11): espejo de index.php +
// cargaDetalle.php + guarda.php del sistema viejo — a diferencia de Mantenimiento/
// Reemplazo, acá SÍ entra AVL/Scada (Modem/Chip). Se elige equipo Inactivo de stock
// y se fija un precio de venta (precargado desde el contrato vigente, editable).
const headCellSx = {
  fontWeight: 800,
  bgcolor: (t) => (t.palette.mode === "dark" ? t.palette.background.paper : t.palette.grey[100]),
};
const tableSx = { "& .MuiTableCell-root": { py: 0.1, px: 0.6, fontSize: 10.5, lineHeight: 1.3 } };
// Input nativo (Box component="input") en vez de TextField para la celda de precio
// sin label — mismo criterio que AsignarEquiposVariosOT.
const inputCompactoSx = {
  fontSize: 11,
  fontFamily: "inherit",
  padding: "4px 6px",
  border: "1px solid",
  borderColor: "divider",
  borderRadius: 0,
  outline: "none",
  bgcolor: "background.paper",
  color: "text.primary",
  "&:focus": { borderColor: "primary.main" },
  "&:disabled": { bgcolor: (t) => t.palette.action.disabledBackground, color: "text.disabled" },
};

// Misma tarjeta con ícono + borde de acento que ya usa AsignarEquiposParOT.jsx —
// layout de 2 columnas pedido explícitamente para que Venta se vea igual.
const SectionCard = ({ title, icon, color = "primary", extra, children }) => (
  <Paper
    variant="outlined"
    sx={{
      p: 1.25,
      borderRadius: 1,
      borderTop: "3px solid",
      borderTopColor: `${color}.main`,
    }}
  >
    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
      <Box
        sx={{
          width: 26,
          height: 26,
          borderRadius: "50%",
          flexShrink: 0,
          display: "grid",
          placeItems: "center",
          bgcolor: (theme) => alpha(theme.palette[color].main, theme.palette.mode === "dark" ? 0.28 : 0.12),
          color: `${color}.main`,
        }}
      >
        {icon}
      </Box>
      <Typography variant="subtitle2" fontWeight={800}>{title}</Typography>
      {extra}
    </Stack>
    {children}
  </Paper>
);

const PAGE_SIZE = 15;
const TIPO_LABEL = { Radio: "Radios", Bateria: "Baterías", Modem: "Módems", Chip: "Chips" };
// Los ítems que devuelve el backend (/vendidos) traen `familia` en mayúsculas (RADIO,
// BATERIA, MODEM, CHIP, VARIOS) — distinto de TIPO_LABEL, que es para los toggles del
// catálogo (Radio, Bateria, ...).
const FAMILIA_LABEL = { RADIO: "Radio", BATERIA: "Batería", MODEM: "Módem", CHIP: "Chip", VARIOS: "Equipo" };

const familiaDe = (t) => ({ Radio: "RADIO", Bateria: "BATERIA", Modem: "MODEM", Chip: "CHIP" }[t] || "VARIOS");

const endpointDeTipo = (tipo) => {
  if (tipo === "Radio") return "/api/equipos-radio";
  if (tipo === "Bateria") return "/api/equipos-bateria";
  if (tipo === "Modem") return "/api/equipos-modem";
  if (tipo === "Chip") return "/api/equipos-chip";
  return "/api/equipos-varios";
};

// Legacy filtra por ciudad en Radio/Bateria/Varios, pero NO en Modem (cargaDetalle.php).
const conCiudad = (tipo) => tipo !== "Modem";

// Hook con toda la lógica + las dos mitades (left/right) ya armadas como JSX, para que
// un padre pueda apilarlas junto a las de VenderAccesoriosOT en una misma columna (ver
// useVenderAccesoriosOT) — así "Equipos Vendidos" y "Accesorios Vendidos" quedan uno
// justo debajo del otro sin depender de la altura de la tabla de la izquierda.
export const useVenderEquipoOT = ({ cod_ot, cod_cliente, cod_servicio_cliente, servicio, esTKR, esAVL, onEnviado, onVendidosCambiados }) => {
  const { user } = useAuth();
  const [tiposOtros, setTiposOtros] = useState([]);
  const tipos = esTKR ? ["Radio", "Bateria"] : esAVL ? ["Modem", "Chip"] : tiposOtros;
  const [tipo, setTipo] = useState("");

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [buscar, setBuscar] = useState("");
  const [buscarAplicado, setBuscarAplicado] = useState("");
  const [loadingEquipos, setLoadingEquipos] = useState(true);
  const [error, setError] = useState("");

  // { [key]: { tipoEquipo, identificador, precioVenta } }
  const [seleccion, setSeleccion] = useState({});
  const [enviando, setEnviando] = useState(false);
  const [errores, setErrores] = useState([]);
  const [equipoDialog, setEquipoDialog] = useState(null);
  const [confirmarOpen, setConfirmarOpen] = useState(false);

  // Equipos ya vendidos en esta OT (confirmados en una tanda anterior) — el técnico
  // puede vender en varias tandas antes de "Finalizar la Venta de Equipos", así que sin
  // esto no hay forma de ver qué ya se vendió sin salir de la pantalla.
  const [vendidos, setVendidos] = useState([]);
  const [loadingVendidos, setLoadingVendidos] = useState(true);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const cargarVendidos = () => {
    setLoadingVendidos(true);
    axios.get(`/api/ot-venta/vendidos/${cod_ot}`)
      .then((res) => setVendidos(res.data || []))
      .catch(() => setVendidos([]))
      .finally(() => setLoadingVendidos(false));
  };

  useEffect(() => { cargarVendidos(); }, [cod_ot]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reporta el conteo al padre para que pueda bloquear "Finalizar la Venta de Equipos"
  // mientras no se vendió nada todavía — mismo patrón que onPlacasCambiadas.
  useEffect(() => {
    if (onVendidosCambiados) onVendidosCambiados(vendidos.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendidos]);

  // "Otros" servicios: catálogo de tipos dinámico (equipo/servicio), igual que
  // AsignarEquiposVariosOT.jsx.
  useEffect(() => {
    if (esTKR || esAVL) return;
    axios.get("/api/equipos-varios/tipos", { params: { servicio } })
      .then((res) => setTiposOtros(res.data || []))
      .catch(() => setTiposOtros([]));
  }, [esTKR, esAVL, servicio]);

  useEffect(() => {
    if (tipos.length && !tipo) setTipo(tipos[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipos.join("|")]);

  const cargarPrecioSugerido = async (fam) => {
    try {
      const { data } = await axios.get("/api/ot-venta/precio-sugerido", {
        params: { cod_servicio_cliente, familia: fam },
      });
      return Number(data?.precio || 0);
    } catch {
      return 0;
    }
  };

  const esTodos = tipo === "Todos";
  const FAMILIAS_FIJAS = ["Radio", "Bateria", "Modem", "Chip"];

  const cargarPagina = (nextPage = page, buscarTexto = buscarAplicado) => {
    if (!tipo) return;
    setLoadingEquipos(true);
    setError("");
    const params = { estado: "Inactivo", buscar: buscarTexto, page: nextPage, pageSize: PAGE_SIZE };
    if (conCiudad(tipo)) params.ciudad = user?.sucursal;
    // /api/equipos-varios necesita `tipo` para distinguir entre los sub-tipos dinámicos
    // (Accesorio POC, CLIP ENRICO, etc.) — los demás endpoints ya son específicos por
    // familia (equipos-radio, equipos-modem, ...) y no lo usan. Con "Todos" se manda la
    // lista completa separada por coma (el backend acepta tipo=a,b,c con IN).
    if (!FAMILIAS_FIJAS.includes(tipo)) params.tipo = esTodos ? tiposOtros.join(",") : tipo;
    axios
      .get(endpointDeTipo(tipo), { params })
      .then((res) => {
        const data = res.data || {};
        const items = data.items || data.rows || [];
        const identificadorDe = (r) => r.serie ?? r.num_sim ?? r.numero;
        setItems(items.map((r) => {
          // Con "Todos" cada fila puede ser de un sub-tipo distinto — se usa el tipo
          // real que trae la fila (r.tipo), no el de la pestaña activa.
          const tipoReal = !FAMILIAS_FIJAS.includes(tipo) ? (r.tipo || tipo) : tipo;
          return {
            key: `${tipoReal.toLowerCase()}-${r.ID ?? r.ID_A ?? identificadorDe(r)}`,
            tipoEquipo: tipoReal,
            identificador: identificadorDe(r),
            modelo: r.modelo,
            ciudad: r.ciudad,
          };
        }));
        setTotal(Number(data.total ?? 0));
        setPage(Number(data.page || nextPage));
      })
      .catch((err) => {
        console.error("Error al cargar equipo Inactivo", err);
        setError("No se pudo cargar el equipo disponible.");
      })
      .finally(() => setLoadingEquipos(false));
  };

  useEffect(() => {
    setBuscar("");
    setBuscarAplicado("");
    cargarPagina(1, "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipo, user?.sucursal]);

  const handleBuscar = () => {
    setBuscarAplicado(buscar.trim());
    cargarPagina(1, buscar.trim());
  };

  const toggleEquipo = async (row) => {
    if (seleccion[row.key]) {
      setSeleccion((prev) => {
        const { [row.key]: _quitado, ...resto } = prev;
        return resto;
      });
      return;
    }
    const precioSugerido = await cargarPrecioSugerido(familiaDe(row.tipoEquipo));
    setSeleccion((prev) => ({
      ...prev,
      [row.key]: { tipoEquipo: row.tipoEquipo, identificador: row.identificador, modelo: row.modelo, precioVenta: precioSugerido },
    }));
  };

  const quitarSeleccion = (key) => {
    setSeleccion((prev) => {
      const { [key]: _quitado, ...resto } = prev;
      return resto;
    });
  };

  const setPrecio = (key, valor) => {
    setSeleccion((prev) => ({ ...prev, [key]: { ...prev[key], precioVenta: valor } }));
  };

  const seleccionadosEntries = Object.entries(seleccion);
  const seleccionados = Object.values(seleccion);

  const handleConfirmarVenta = async () => {
    if (!seleccionados.length) return;
    setEnviando(true);
    setErrores([]);
    const fallidos = [];
    const exitosos = [];
    for (const [key, item] of Object.entries(seleccion)) {
      try {
        await axios.post("/api/ot-venta/vender", {
          cod_ot,
          familia: familiaDe(item.tipoEquipo),
          identificador: item.identificador,
          cod_cliente,
          cod_servicio_cliente,
          precioVenta: Number(item.precioVenta) || 0,
          tipoEquipoVarios: item.tipoEquipo,
        });
        exitosos.push(key);
      } catch (err) {
        fallidos.push(err?.response?.data?.error || `Error al vender "${item.identificador}"`);
      }
    }
    if (exitosos.length) {
      setSeleccion((prev) => {
        const next = { ...prev };
        exitosos.forEach((k) => delete next[k]);
        return next;
      });
      cargarPagina(page, buscarAplicado);
      cargarVendidos();
      if (onEnviado) onEnviado();
    }
    setErrores(fallidos);
    setEnviando(false);
    if (!fallidos.length) setConfirmarOpen(false);
  };

  const left = (
    <>
      {error && <Typography color="error" variant="body2" sx={{ mb: 1 }}>{error}</Typography>}
      <SectionCard title="Equipos Disponibles para la Venta" icon={<Inventory2Icon fontSize="small" />} color="primary">
            {tipos.length > 1 && (
              <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  color="primary"
                  value={tipo}
                  onChange={(e, nuevo) => nuevo && setTipo(nuevo)}
                  sx={{ flexWrap: "wrap", justifyContent: "center", gap: 0.5, rowGap: 0.5 }}
                >
                  {!esTKR && !esAVL && tiposOtros.length > 1 && (
                    <ToggleButton
                      value="Todos"
                      sx={{ fontSize: 11.5, px: 1.5, py: 0.35, textTransform: "none", whiteSpace: "nowrap", borderRadius: "4px !important", border: "1px solid !important", fontWeight: 700 }}
                    >
                      Todos
                    </ToggleButton>
                  )}
                  {tipos.map((t) => (
                    <ToggleButton
                      key={t}
                      value={t}
                      sx={{ fontSize: 11.5, px: 1.5, py: 0.35, textTransform: "none", whiteSpace: "nowrap", borderRadius: "4px !important", border: "1px solid !important" }}
                    >
                      {TIPO_LABEL[t] || t}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Box>
            )}

            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center" sx={{ mb: 1, rowGap: 1 }}>
              <TextField
                size="small"
                label="Buscar"
                value={buscar}
                onChange={(e) => setBuscar(e.target.value)}
                onKeyDown={(e) => {
                  // Enter simple inserta salto de línea (para poder pegar/escribir
                  // varios identificadores); Ctrl/Cmd+Enter dispara la búsqueda —
                  // mismo criterio que AsignarEquiposVariosOT.jsx / ListadoEquipos.jsx.
                  if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) handleBuscar();
                }}
                placeholder="Serie, EMAI... (pega una columna de Excel)"
                multiline
                minRows={1}
                maxRows={4}
                sx={{
                  minWidth: 220,
                  "& .MuiInputLabel-root": { fontSize: 11 },
                  "& .MuiOutlinedInput-root": { minHeight: 30, fontSize: 11.5, alignItems: "flex-start" },
                }}
              />
              <IconButton size="small" onClick={handleBuscar} title="Buscar"><SearchIcon fontSize="small" /></IconButton>
              <Chip size="small" label={`Total: ${total}`} sx={{ height: 20, fontSize: 11 }} />
              <Chip size="small" color="warning" variant="outlined" label={`Seleccionados: ${seleccionados.length}`} sx={{ height: 20, fontSize: 11 }} />
              <Box sx={{ flexGrow: 1 }} />
              <Typography variant="caption" color="text.secondary">
                Página {total ? page : 0} / {total ? totalPages : 0}
              </Typography>
              <Button size="small" variant="outlined" startIcon={<NavigateBeforeIcon />} disabled={loadingEquipos || page <= 1}
                onClick={() => cargarPagina(page - 1)} sx={{ minHeight: 26, height: 26, fontSize: 11 }}>
                Anterior
              </Button>
              <Button size="small" variant="outlined" endIcon={<NavigateNextIcon />} disabled={loadingEquipos || page >= totalPages}
                onClick={() => cargarPagina(page + 1)} sx={{ minHeight: 26, height: 26, fontSize: 11 }}>
                Siguiente
              </Button>
            </Stack>

            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 0.5 }}>
              <Table size="small" sx={tableSx}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ ...headCellSx, width: 32 }}>Sel.</TableCell>
                    <TableCell sx={headCellSx}>Identificador</TableCell>
                    <TableCell sx={headCellSx}>Modelo</TableCell>
                    {esTodos && <TableCell sx={headCellSx}>Tipo</TableCell>}
                    <TableCell sx={{ ...headCellSx, width: 140 }}>Precio de venta</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loadingEquipos ? (
                    <TableRow><TableCell colSpan={esTodos ? 5 : 4} align="center" sx={{ py: 2 }}><CircularProgress size={18} /></TableCell></TableRow>
                  ) : !items.length ? (
                    <TableRow>
                      <TableCell colSpan={esTodos ? 5 : 4}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                          {esTodos
                            ? "No hay equipos Inactivos disponibles."
                            : `No hay ${(TIPO_LABEL[tipo] || tipo || "").toLowerCase()} Inactivo(a)s disponibles.`}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    items.map((row) => {
                      const datos = seleccion[row.key];
                      const checked = Boolean(datos);
                      return (
                        <TableRow key={row.key} hover selected={checked}>
                          <TableCell>
                            <Checkbox size="small" checked={checked} onChange={() => toggleEquipo(row)} sx={{ p: 0 }} />
                          </TableCell>
                          <TableCell>
                            <Box
                              component="span"
                              onClick={() => setEquipoDialog({ tipo: row.tipoEquipo.toLowerCase(), identificador: row.identificador })}
                              sx={{ cursor: "pointer", color: "primary.main", textDecoration: "underline" }}
                            >
                              {row.identificador}
                            </Box>
                          </TableCell>
                          <TableCell>{row.modelo}</TableCell>
                          {esTodos && <TableCell>{row.tipoEquipo}</TableCell>}
                          <TableCell>
                            <Box
                              component="input"
                              type="number"
                              disabled={!checked}
                              value={datos?.precioVenta ?? ""}
                              onChange={(e) => setPrecio(row.key, e.target.value)}
                              sx={{ ...inputCompactoSx, width: 90 }}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
              <Button
                size="small"
                variant="contained"
                color="success"
                disabled={!seleccionados.length}
                onClick={() => setConfirmarOpen(true)}
                sx={{ minHeight: 28, height: 28, fontSize: 11.5, px: 2 }}
              >
                Vender Equipos Seleccionados
              </Button>
            </Box>
      </SectionCard>
    </>
  );

  const right = (
    <>
      <SectionCard
            title="Equipos Vendidos en esta OT"
            icon={<AssignmentTurnedInIcon fontSize="small" />}
            color="success"
            extra={
              <Chip size="small" color="success" label={vendidos.length} sx={{ height: 20, fontSize: 11, fontWeight: 700 }} />
            }
          >
            {loadingVendidos ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                <CircularProgress size={18} />
              </Box>
            ) : !vendidos.length ? (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                Todavía no se vendió ningún equipo en esta OT.
              </Typography>
            ) : (
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 360, borderRadius: 0.5 }}>
                <Table size="small" stickyHeader sx={tableSx}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={headCellSx}>Identificador</TableCell>
                      <TableCell sx={headCellSx}>Tipo</TableCell>
                      <TableCell sx={{ ...headCellSx, width: 90 }} align="right">Precio</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {vendidos.map((r) => (
                      <TableRow key={`${r.familia}-${r.identificador}`} hover>
                        <TableCell>
                          <Box
                            component="span"
                            onClick={() => setEquipoDialog({ tipo: r.familia.toLowerCase(), identificador: r.identificador })}
                            sx={{ cursor: "pointer", color: "primary.main", textDecoration: "underline" }}
                          >
                            {r.identificador}
                          </Box>
                        </TableCell>
                        <TableCell>{FAMILIA_LABEL[r.familia] || r.familia}</TableCell>
                        <TableCell align="right">{Number(r.precio_venta || 0).toFixed(2)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
      </SectionCard>
    </>
  );

  const dialogs = (
    <>
      <Dialog open={confirmarOpen} onClose={() => !enviando && setConfirmarOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>Confirmar Venta de Equipos</DialogTitle>
        <DialogContent>
          {!!errores.length && (
            <MuiAlert severity="error" variant="outlined" sx={{ mb: 1, fontSize: 11, py: 0 }}>
              {errores.map((msg, i) => (<div key={i}>{msg}</div>))}
            </MuiAlert>
          )}
          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 320, borderRadius: 0.5 }}>
            <Table size="small" stickyHeader sx={tableSx}>
              <TableHead>
                <TableRow>
                  <TableCell sx={headCellSx}>Tipo</TableCell>
                  <TableCell sx={headCellSx}>Identificador</TableCell>
                  <TableCell sx={headCellSx}>Modelo</TableCell>
                  <TableCell sx={{ ...headCellSx, width: 130 }}>Precio de venta</TableCell>
                  <TableCell sx={{ ...headCellSx, width: 40 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {seleccionadosEntries.map(([key, datos]) => (
                  <TableRow key={key} hover>
                    <TableCell>{TIPO_LABEL[datos.tipoEquipo] || datos.tipoEquipo}</TableCell>
                    <TableCell>{datos.identificador}</TableCell>
                    <TableCell>{datos.modelo}</TableCell>
                    <TableCell>
                      <Box
                        component="input"
                        type="number"
                        value={datos.precioVenta ?? ""}
                        onChange={(e) => setPrecio(key, e.target.value)}
                        sx={{ ...inputCompactoSx, width: 90 }}
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={() => quitarSeleccion(key)} title="Quitar">
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={() => setConfirmarOpen(false)} disabled={enviando}>Cancelar</Button>
          <Button
            size="small"
            variant="contained"
            color="success"
            disabled={!seleccionados.length || enviando}
            onClick={handleConfirmarVenta}
          >
            {enviando ? <CircularProgress size={16} color="inherit" /> : "Confirmar Venta"}
          </Button>
        </DialogActions>
      </Dialog>

      <CentroEquipoDetalleDialog
        open={!!equipoDialog}
        tipo={equipoDialog?.tipo}
        identificador={equipoDialog?.identificador}
        onClose={() => setEquipoDialog(null)}
      />
    </>
  );

  return { left, right, dialogs };
};

const VenderEquipoOT = (props) => {
  const { left, right, dialogs } = useVenderEquipoOT(props);
  return (
    <Box sx={{ mt: 1 }}>
      <Grid container spacing={1.5}>
        <Grid size={{ xs: 12, md: 8 }}>{left}</Grid>
        <Grid size={{ xs: 12, md: 4 }}>{right}</Grid>
      </Grid>
      {dialogs}
    </Box>
  );
};

export default VenderEquipoOT;
